import { NEUTRAL_K99_WITHOUT_TOKEN } from './pendingTokens';
import type { SemanticColourTheme } from './types';

/**
 * The `background` and `border` vocabularies for `Box` (and everything built on
 * it — `Stack`, `Flex`, `Inline`, `Columns`, `Grid`, `PageBlock`).
 *
 * Unlike the `color` vocabulary in `./textColor`, these keys are NOT renamed to
 * semantic token names, because most of them have no semantic token to be named
 * after: of the 20 background keys only 8 resolve to one, and of the 8 border
 * keys only `standard` does. Renaming half a vocabulary would leave the prop
 * speaking two languages at once, so the keys keep their current names and this
 * module owns the mapping instead. RWR-18028.
 *
 * Values below marked WITHOUT_TOKEN are the legacy `palette` literals, held so
 * `useBoxStyles` can stop reading the `palette` object — in this file where only
 * `Box` uses them, or in `./pendingTokens` where another component shares the same
 * value (`NEUTRAL_K99_WITHOUT_TOKEN`). They are the exact
 * values `palette` resolved to, so nothing changes visually — but unlike the
 * mapped keys they are no longer driven by a CSS custom property, so they cannot
 * be themed until a real token exists. Nothing in the repo overrides the
 * `--palette-*` properties (the only hits are in a self-contained static 404
 * page that does not use `Box`), so that is not a regression today.
 *
 * Every WITHOUT_TOKEN entry is a Figma ask. The largest by far is
 * `background.muted` — the neutral `k99` step, 60 call sites. It cannot be
 * snapped to `surface.neutral.background1` even though it is only 4.7 away,
 * because `background.dim` already *is* `background1`; collapsing them would
 * erase the muted/dim distinction across ~100 sites.
 */

/** Pale selection/status surface tiers, and the lilac accent. No semantic equivalents. */
const SURFACE_TIERS_WITHOUT_TOKENS = {
  accent: '#e0deed',
  selectableSelected: '#f5faff',
  selectableSelectedHover: '#e9f1ff',
  active: '#d6eaff',
  activeMuted: '#d0e4ff',
  cautiousMuted: '#ffefd4',
  criticalMuted: '#f9cdd0',
  informativeMuted: '#d0e4ff',
  positiveMuted: '#cceadd',
} as const;

/**
 * The status border tier. `muted` is listed here rather than mapped because its
 * value matches only `surface.neutral.background1` — a *background* token, so
 * using it as a border colour would assert a role the token does not have.
 */
const BORDERS_WITHOUT_TOKENS = {
  muted: '#f4f5f7',
  accent: '#d0cee5',
  active: '#c2dfff',
  cautious: '#ffeccb',
  critical: '#f8c3c6',
  informative: '#c2e5fa',
  positive: '#c2e6d6',
} as const;

export const getBoxBackgrounds = (colour: SemanticColourTheme) => ({
  base: colour.surface.neutral.background,
  dim: colour.surface.neutral.background1,
  selectablePressed: colour.surface.neutral.background1,
  cautious: colour.reserved.warning.container.background,
  critical: colour.reserved.critical.container.background,
  informative: colour.reserved.info.container.background,
  positive: colour.reserved.success.container.background,
  /** The light primary-tinted container surface (primary k98). */
  primary: colour.surface.primary.background1,
  primaryContainer: colour.primary.container.background,

  /** #eef1f8 -> #ECEFF9, 3.0 away. Same snap already shipped in #6864. */
  shade: colour.surface.neutral.background2,
  /** #FCFBFF -> #ffffff, 5.0 away. Same snap already shipped in #6864. */
  dialog: colour.surface.neutral.background,

  muted: NEUTRAL_K99_WITHOUT_TOKEN,
  selectableHover: NEUTRAL_K99_WITHOUT_TOKEN,
  ...SURFACE_TIERS_WITHOUT_TOKENS,
});

export const getBoxBorders = (colour: SemanticColourTheme) => ({
  standard: colour.surface.neutral.border,
  // Selected/emphasised container border - the brand purple. `background` is
  // its only semantic carrier today (a role mismatch like `muted` above);
  // point this at a real primary border token when one lands.
  primary: colour.primary.background,
  ...BORDERS_WITHOUT_TOKENS,
});

export type BoxBackgroundKey = keyof ReturnType<typeof getBoxBackgrounds>;
export type BoxBorderKey = keyof ReturnType<typeof getBoxBorders>;
